import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';

import { getReplies } from 'api/Comment';

import { cn, ia, iaRa } from 'lib/helpers/utility';

import AvatarGroup from 'components/shared/AvatarGroup/AvatarGroup';
import Button from 'components/shared/Button/Button';
import { MYRIAD_STAFF_AVATAR_ID } from 'components/shared/ExpandableToolbar/lib/helpers';
import Typography from 'components/shared/Typography/Typography';
import state from 'components/state';

import { useComment } from '../useComment';

import Comment from './Comment';

const Replies = ({
  commentId,
  repliesCount: repliesCountProp,
  replyAuthors,
  parentCreatedAt,
  category,
  refId,
  isExpanded,
  onToggleExpanded,
  openCommentInputHandler
}) => {
  const navigate = useNavigate();
  const userState = useRecoilValue(state.userState);
  const permission = useRecoilValue(state.permissions);
  const isViewerAdmin = !!permission?.admin_id;
  const currentUserId = permission?.admin_id || userState?.id;

  const { data, isLoading, isError } = useQuery(
    ['comment-replies', commentId],
    () => getReplies(navigate, { commentId }),
    { enabled: isExpanded, refetchOnMount: false, refetchOnWindowFocus: false, staleTime: 30000 }
  );

  const { onCommentDeleteHandler } = useComment(category, refId);

  const onReplyDeleteHandler = (replyId) =>
    onCommentDeleteHandler(replyId, { isReply: true, parentCommentId: commentId });

  const replies = iaRa(data?.replies);

  const repliesAuthors = useMemo(() => {
    const repliesToDisplay = data?.replies ? replies : iaRa(replyAuthors);

    const seenAuthors = new Set();
    const authors = [];
    let hasOtherAdmin = false;

    for (const reply of repliesToDisplay) {
      const creator = reply?.creator;
      if (!creator?.id || seenAuthors.has(creator.id)) continue;

      // if user is not an admin show admin comments as myriad staff avatar
      if (!isViewerAdmin && !reply.created_by_type && reply.creator.id !== currentUserId) {
        hasOtherAdmin = true;
        continue;
      }

      seenAuthors.add(creator.id);
      authors.push({ ...creator, image: creator?.image || creator.profile_photo });
    }

    if (hasOtherAdmin) {
      authors.push({ id: MYRIAD_STAFF_AVATAR_ID, f_name: 'Myriad', l_name: 'Staff' });
    }

    return authors;
  }, [replies, replyAuthors, isViewerAdmin, currentUserId]);

  const repliesCount = (data?.replies ? replies.length : repliesCountProp) || 0;

  // Don't render anything if not expanded and no replies.
  if (!isExpanded && repliesCount === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      {(isExpanded || repliesCount > 0) && (
        <div className="flex items-center gap-1.5">
          {ia(repliesAuthors) && (
            <AvatarGroup
              items={repliesAuthors}
              itemsToShow={3}
              size="small"
              className="pointer-events-none"
            />
          )}

          <Button
            size="xs"
            variant="link"
            color="neutral"
            text={
              isExpanded && repliesCount === 0
                ? 'Hide replies'
                : `${repliesCount} ${repliesCount === 1 ? 'reply' : 'replies'}`
            }
            iconRight={isExpanded ? 'chevron-up-linear' : 'chevron-down-linear'}
            onClick={onToggleExpanded}
            className="h-6 text-xs"
            textClassName="text-neutral-900"
          />
        </div>
      )}

      {isExpanded && (
        <div
          className={cn(
            'ml-[9px] flex w-full flex-col gap-4 pl-[13px]',
            !isLoading && repliesCount > 0 && 'border-0 border-l border-solid border-neutral-300'
          )}>
          {isLoading ? (
            <Typography color="neutral-500" size="small">
              Loading replies...
            </Typography>
          ) : isError ? (
            <Typography color="neutral-500" size="small">
              Failed to load replies.
            </Typography>
          ) : repliesCount > 0 ? (
            replies.map((reply) => (
              <Comment
                isReply
                key={reply.id}
                comment={reply}
                parentCreatedAt={parentCreatedAt}
                category={category}
                refId={refId}
                onDelete={onReplyDeleteHandler}
                openCommentInputHandler={openCommentInputHandler}
                parentCommentId={commentId}
              />
            ))
          ) : (
            <Typography color="neutral-500" size="small">
              No replies yet
            </Typography>
          )}
        </div>
      )}
    </div>
  );
};

export default Replies;
